import { useCallback, useState } from 'react';
import {
  usePaymentMethod,
  usePaymentEvent,
  useBillingHistory,
  type SubscriptionUpdatePreviewT,
} from '@mentimeter/billing';
import { PurchaseSummary } from '@mentimeter/checkout-ui';
import { useUser } from '@mentimeter/user';
import { PaymentMethodType } from '@mentimeter/http-clients';
import { useTranslations } from 'next-intl';
import { captureException } from '@mentimeter/errors/sentry';
import { MentiError } from '@mentimeter/error-utils/error';
import {
  trackSubmitting,
  updateStripe,
  updateSubscription,
} from '../data-hooks/hooks';
import type { CheckoutModalType } from '../types';
import { trackPurchase } from '../google-tracking/purchase-tracking';

interface PurchaseSummaryDataHandlerT {
  isLoading: boolean;
  fetchingError: Error | undefined;
  type: CheckoutModalType;
  numberOfLicenses: number;
  invoicePreview: SubscriptionUpdatePreviewT | undefined;
  onSubmitSuccess: (receiptUrl: string) => void;
}

export const PurchaseSummaryDataHandler = ({
  isLoading,
  fetchingError,
  type,
  invoicePreview,
  numberOfLicenses,
  onSubmitSuccess,
}: PurchaseSummaryDataHandlerT) => {
  const t = useTranslations('purchasing.legacy.checkoutModalPackage');

  const { user } = useUser();
  const { paymentMethod } = usePaymentMethod(!user || user?.isFreeUser);
  const { mutate: mutateBillingHistory } = useBillingHistory();

  const [purchaseLoading, setPurchaseLoading] = useState<boolean>(false);
  const [purchaseError, setPurchaseError] = useState<string>();

  // Listen for subscription update to be done and fetch the newest receipt
  usePaymentEvent('subscription.updated', () => {
    mutateBillingHistory().then((newReceipts) => {
      if (newReceipts?.[0]?.invoice_url) {
        onSubmitSuccess(newReceipts[0].invoice_url);
      } else {
        // If we for some reason don't get a receipt in time, we still want to show the success state
        // so we can show the user that the purchase was successful - We log this to Sentry so we can investigate how often this happens
        onSubmitSuccess('no_receipt');
        captureException(
          new MentiError('No receipt received after checkout modal purchase', {
            feature: 'payments',
            tags: { invoiceUrl: String(newReceipts?.[0]?.invoice_url) },
          }),
        );
      }
      setPurchaseLoading(false);
    });
  });

  const submitPurchase = useCallback(async () => {
    if (!paymentMethod) {
      return setPurchaseError(t('please_add_payment_method'));
    }
    setPurchaseLoading(true);

    if (!invoicePreview) {
      setPurchaseLoading(true);

      if (!isLoading) {
        setPurchaseLoading(false);
        setPurchaseError(t('default_error_message'));
      }

      return;
    }

    trackSubmitting();

    const newData = {
      licenses: invoicePreview.purchaseItem.quantity,
      plan_category:
        type === 'licenses'
          ? invoicePreview.prorationItem.category
          : invoicePreview.purchaseItem.category,
      proration_date: invoicePreview.prorationDate,
    };

    const [errorResponse, successResponse] = await updateSubscription(newData);

    if (errorResponse) {
      setPurchaseLoading(false);
      return setPurchaseError(t('default_error_message'));
    }

    if (successResponse?.data.payment_intent_client_secret) {
      const errorMessage = await updateStripe(
        successResponse.data.payment_intent_client_secret,
        t('default_error_message'),
        paymentMethod,
      );

      trackPurchase(
        type,
        numberOfLicenses,
        invoicePreview,
        paymentMethod,
        user?.id,
        successResponse.data.request_id,
      );

      if (errorMessage) {
        setPurchaseLoading(false);
        return setPurchaseError(errorMessage);
      }
    }
  }, [
    paymentMethod,
    invoicePreview,
    type,
    t,
    isLoading,
    numberOfLicenses,
    user?.id,
  ]);

  return (
    <PurchaseSummary
      isLoading={isLoading}
      purchaseLoading={purchaseLoading}
      paymentMethod={paymentMethod?.type ?? PaymentMethodType.Card}
      invoicePreview={invoicePreview}
      purchaseError={purchaseError ?? ''}
      fetchingError={fetchingError}
      onSubmit={submitPurchase}
    />
  );
};
